import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

function CuisineRow({ row, onSaved }) {
  const [text, setText] = useState(row.intro || "");
  const [busy, setBusy] = useState("");
  const [hero, setHero] = useState(row.hero_url);
  const [alt, setAlt] = useState(row.hero_alt || "");
  const [savedAlt, setSavedAlt] = useState(row.hero_alt || "");
  const fileRef = useRef(null);
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    setBusy("");
  };
  const save = () => run("save", () => api.put(`/admin/cuisines/${row.slug}/intro`, { intro: text }), `Saved intro for ${row.cuisine}`);
  const regen = () => run("regen", async () => { const { data } = await api.post(`/admin/cuisines/${row.slug}/intro/regenerate`); setText(data.intro); }, "New intro written");
  const gotHero = (data) => { setHero(data.hero_url); setAlt(data.hero_alt || ""); setSavedAlt(data.hero_alt || ""); };
  const regenHero = () => run("hero", async () => { const { data } = await api.post(`/admin/cuisines/${row.slug}/hero/regenerate`); gotHero(data); }, "New header photo made");
  const saveAlt = () => run("alt", async () => { const { data } = await api.put(`/admin/cuisines/${row.slug}/alt`, { alt }); setSavedAlt(data.hero_alt || ""); }, "Alt text saved");
  const upload = (f) => f && run("upload", async () => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post(`/admin/cuisines/${row.slug}/hero`, fd);
    gotHero(data);
  }, "Header photo uploaded");
  const btn = "rounded-full border border-rf-ink/20 px-3 py-1 text-sm font-bold hover:bg-rf-cream-alt disabled:opacity-50";
  return (
    <li className="card-recipe !p-4 grid md:grid-cols-[220px_1fr] gap-4" data-testid={`cuisine-admin-row-${row.slug}`}>
      <div>
        <div className="aspect-[16/9] rounded-xl overflow-hidden bg-rf-cream-alt flex items-center justify-center" data-testid={`cuisine-admin-hero-${row.slug}`}>
          {hero ? <img src={hero} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-rf-ink-soft px-3 text-center">No photo yet. The AI makes one within 10 minutes.</span>}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <button type="button" disabled={!!busy} onClick={regenHero} className={btn} data-testid={`cuisine-hero-regen-${row.slug}`}>{busy === "hero" ? "Painting…" : "New AI photo"}</button>
          <button type="button" disabled={!!busy} onClick={() => fileRef.current?.click()} className={btn} data-testid={`cuisine-hero-upload-${row.slug}`}>{busy === "upload" ? "Uploading…" : "Upload"}</button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {hero && (
          <div className="mt-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-rf-ink-soft" htmlFor={`alt-${row.slug}`}>Photo alt text</label>
            <textarea id={`alt-${row.slug}`} value={alt} onChange={(e) => setAlt(e.target.value)} rows={3} maxLength={150} data-testid={`cuisine-hero-alt-${row.slug}`}
                      placeholder="Describe the photo for Google and screen readers" className="w-full rounded-lg border border-rf-ink/20 px-2 py-1.5 text-xs" />
            <button type="button" disabled={!!busy || alt.trim() === savedAlt.trim()} onClick={saveAlt} className="btn-amber !py-0.5 !px-3 text-xs disabled:opacity-50" data-testid={`cuisine-hero-alt-save-${row.slug}`}>Save alt</button>
          </div>
        )}
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <a href={`/cuisine/${row.slug}`} target="_blank" rel="noreferrer" className="font-bold text-rf-ink hover:text-rf-orange">{row.cuisine}</a>
          <span className="text-xs text-rf-ink-soft">{row.count} recipes{row.source ? ` · ${row.source === "admin" ? "edited by admin" : "AI-written"}` : row.count >= 2 ? " · waiting for AI" : " · intro starts at 2 recipes"}</span>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid={`cuisine-intro-text-${row.slug}`}
                  placeholder="No intro yet. Write your own, or tap Regenerate."
                  className="w-full rounded-lg border border-rf-ink/20 px-3 py-2 text-sm" />
        {(row.faqs || []).length > 0 && (
          <details className="mt-1 text-xs text-rf-ink-soft">
            <summary className="cursor-pointer font-bold">{row.faqs.length} FAQs on this page</summary>
            <ul className="mt-1 space-y-1">{row.faqs.map((f, i) => <li key={i}><b className="text-rf-ink">{f.q}</b> — {f.a}</li>)}</ul>
          </details>
        )}
        <div className="flex gap-2 mt-2">
          <button type="button" disabled={!!busy || text.trim() === (row.intro || "").trim()} onClick={save} className="btn-amber !py-1 !px-3 text-sm disabled:opacity-50" data-testid={`cuisine-intro-save-${row.slug}`}>Save</button>
          <button type="button" disabled={!!busy} onClick={regen} className={btn} data-testid={`cuisine-intro-regen-${row.slug}`}>{busy === "regen" ? "Writing…" : "Regenerate intro + FAQs"}</button>
        </div>
      </div>
    </li>
  );
}

export default function CuisineAdmin() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/cuisines").then(({ data }) => setRows(data.cuisines || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mt-12" data-testid="cuisine-admin-section">
      <h3 className="font-display text-xl font-black text-rf-ink mb-1">Cuisine pages</h3>
      <p className="text-sm text-rf-ink-soft mb-4">Each cuisine has a page at /cuisine/name with a header photo, intro and FAQs. Edit the intro, or swap the photo for a new AI one or your own.</p>
      {rows === null ? <p className="text-sm text-rf-ink-soft">Loading…</p> : rows.length ? (
        <ul className="grid gap-3">{rows.map((r) => <CuisineRow key={r.slug} row={r} onSaved={load} />)}</ul>
      ) : <p className="text-sm text-rf-ink-soft">No cuisines with public recipes yet.</p>}
    </section>
  );
}
